<template>
  <div
    class="kanban-column"
    :class="{
      'kanban-column--collapsed': column.isCollapsed,
      'kanban-column--drag-over': !reorderMode && isDragOver,
      'kanban-column--reorder-over': reorderMode && isReorderOver,
      'kanban-column--reordering': reorderMode,
    }"
    :draggable="reorderMode ? 'true' : 'false'"
    @dragstart="reorderMode ? onColumnDragStart($event) : undefined"
    @dragover.prevent="reorderMode ? onColumnDragOver($event) : onDragOver()"
    @dragleave="reorderMode ? onColumnDragLeave($event) : onDragLeave($event)"
    @drop.prevent="reorderMode ? onColumnDrop($event) : onDrop($event)"
    @dragend="reorderMode ? $emit('column-drop') : undefined"
  >
    <!-- Header -->
    <div
      class="kanban-column__header"
      :style="{ borderTopColor: column.color }"
    >
      <div class="kanban-column__header-left">
        <!-- Drag handle (reorder mode only) -->
        <i
          v-if="reorderMode"
          class="i-lucide-grip-vertical size-4 text-n-slate-8 cursor-grab mr-1 flex-shrink-0"
          title="Arrastar para reordenar"
        />
        <span
          class="kanban-column__dot"
          :style="{ backgroundColor: column.color }"
        />
        <span v-if="!column.isCollapsed" class="kanban-column__title">{{ column.name }}</span>
        <span class="kanban-column__count">{{ cards.length }}</span>
        <span
          v-if="column.wipLimit && cards.length > column.wipLimit && !column.isCollapsed"
          class="kanban-column__wip-exceeded"
          title="Limite WIP excedido"
        >!</span>
      </div>
      <div class="kanban-column__header-actions">
        <button
          v-if="!column.isCollapsed"
          class="kanban-column__action-btn"
          title="Configurar coluna"
          @click="$emit('column-edit', column)"
        >
          <i class="i-lucide-settings-2 size-3.5" />
        </button>
        <button
          v-if="loading"
          class="kanban-column__action-btn"
          disabled
        >
          <i class="i-lucide-loader-2 size-4 animate-spin" />
        </button>
        <button
          v-else
          class="kanban-column__action-btn"
          :title="column.isCollapsed ? 'Expandir' : 'Recolher'"
          @click="$emit('toggle-collapse', column.id)"
        >
          <i :class="column.isCollapsed ? 'i-lucide-chevron-right' : 'i-lucide-chevron-down'" class="size-4" />
        </button>
      </div>
    </div>

    <!-- WIP bar -->
    <div v-if="column.wipLimit && !column.isCollapsed" class="kanban-column__wip-bar">
      <div
        class="kanban-column__wip-fill"
        :style="{
          width: `${Math.min(100, (cards.length / column.wipLimit) * 100)}%`,
          backgroundColor: cards.length > column.wipLimit ? '#EF4444' : column.color,
        }"
      />
    </div>

    <!-- Cards -->
    <div v-if="!column.isCollapsed" class="kanban-column__body">
      <div v-if="!loading && cards.length === 0" class="kanban-column__empty">
        <i class="i-lucide-inbox size-5 mb-1 text-n-slate-7" />
        <span>Sem conversas</span>
      </div>
      <KanbanCard
        v-for="card in cards"
        :key="card.id"
        :conversation="card"
        @click="$emit('card-click', $event)"
      />
    </div>

    <!-- Collapsed view -->
    <div v-else class="kanban-column__collapsed-info">
      <span class="kanban-column__collapsed-count">{{ cards.length }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useKanbanStore } from 'dashboard/stores/kanban';
import KanbanCard from './KanbanCard.vue';

const props = defineProps({
  column: { type: Object, required: true },
  cards: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  reorderMode: { type: Boolean, default: false },
});

const emit = defineEmits(['card-click', 'card-moved', 'toggle-collapse', 'column-edit',
  'column-drag-start', 'column-drag-over', 'column-drop']);
const kanbanStore = useKanbanStore();

// ── Card drag (normal mode) ──────────────────────────────────────────────────
const isDragOver = computed(
  () => !props.reorderMode && kanbanStore.dragState.overColumnId === props.column.id
);

function onDragOver() {
  if (props.reorderMode) return;
  kanbanStore.setDragOver(props.column.id);
}

function onDragLeave(event) {
  if (props.reorderMode) return;
  if (!event.currentTarget.contains(event.relatedTarget)) {
    if (kanbanStore.dragState.overColumnId === props.column.id) {
      kanbanStore.setDragOver(null);
    }
  }
}

function onDrop(event) {
  if (props.reorderMode) return;
  const data = event.dataTransfer.getData('text/plain');
  if (!data) return;
  try {
    const { conversationId, fromStatus } = JSON.parse(data);
    if (conversationId && props.column.id) {
      emit('card-moved', { conversationId, fromStatus, toColumnId: props.column.id });
    }
  } catch {}
  kanbanStore.clearDrag();
}

// ── Column drag (reorder mode) ───────────────────────────────────────────────
const isReorderOver = ref(false);

function onColumnDragStart(event) {
  event.dataTransfer.setData('text/plain', props.column.id);
  event.dataTransfer.effectAllowed = 'move';
  emit('column-drag-start', props.column.id);
}

function onColumnDragOver(event) {
  if (!props.reorderMode) return;
  isReorderOver.value = true;
  event.dataTransfer.dropEffect = 'move';
  emit('column-drag-over', props.column.id);
}

function onColumnDragLeave(event) {
  if (!props.reorderMode) return;
  if (!event.currentTarget.contains(event.relatedTarget)) {
    isReorderOver.value = false;
  }
}

function onColumnDrop(event) {
  if (!props.reorderMode) return;
  isReorderOver.value = false;
  event.dataTransfer.getData('text/plain'); // consume
  emit('column-drop');
}
</script>

<style scoped>
.kanban-column {
  @apply flex flex-col bg-n-slate-2 rounded-xl border border-n-weak
         min-w-[260px] max-w-[300px] w-[280px] flex-shrink-0;
  /* Use width transition only to avoid repainting other elements */
  transition: min-width 0.2s ease, width 0.2s ease;
  border-top: 3px solid transparent;
}
.kanban-column--collapsed {
  min-width: 56px !important;
  width: 56px !important;
}
.kanban-column--drag-over {
  @apply border-n-iris-8 ring-1 ring-n-iris-8;
}
.kanban-column--reorder-over {
  @apply border-n-amber-8 ring-1 ring-n-amber-8;
}
.kanban-column--reordering {
  @apply cursor-grab;
}

.kanban-column__header {
  @apply flex items-center justify-between px-3 py-2.5 rounded-t-xl;
  border-top-width: 3px;
  border-top-style: solid;
}
.kanban-column__header-left {
  @apply flex items-center gap-1.5 min-w-0 overflow-hidden flex-1;
}
.kanban-column__dot {
  @apply flex-shrink-0 w-2 h-2 rounded-full;
}
.kanban-column__title {
  @apply text-sm font-semibold text-n-slate-12 truncate;
}
.kanban-column__count {
  @apply flex-shrink-0 text-xs font-medium text-n-slate-10 bg-n-slate-4 rounded-full
         px-1.5 py-0.5;
}
.kanban-column__wip-exceeded {
  @apply flex-shrink-0 text-xs font-bold text-red-500;
}
.kanban-column__header-actions {
  @apply flex items-center;
}
.kanban-column__action-btn {
  @apply p-0.5 rounded hover:bg-n-slate-4 text-n-slate-9 hover:text-n-slate-12 transition-colors;
}

.kanban-column__wip-bar {
  @apply h-0.5 bg-n-slate-4 mx-3;
}
.kanban-column__wip-fill {
  @apply h-full transition-all duration-300;
}

.kanban-column__body {
  @apply flex-1 overflow-y-auto px-2.5 py-2 min-h-[80px] max-h-[calc(100vh-220px)];
}
.kanban-column__empty {
  @apply flex flex-col items-center justify-center py-8 text-xs text-n-slate-9;
}

.kanban-column__collapsed-info {
  @apply flex items-center justify-center py-4;
}
.kanban-column__collapsed-count {
  @apply text-sm font-semibold text-n-slate-10;
}
</style>
